import type { Broadcast, BroadcastStatus } from '../../types/broadcast';
import type { BroadcastSend } from '../../types/broadcastSend';
import type {
  ChannelCounters,
  DeleteAction,
  DuplicateAction,
  SendResultsChannel,
  SendResultsData,
  SendResultsRecipient,
  SendResultsStatus,
} from '../../types/sendResults';

// Stage 6A v5 §4.1 — broadcast adapter. Pure: Broadcast + its BroadcastSend list
// + a precomputed audience description → SendResultsData. The duplicate action
// is injected by the route (it owns the mutation + navigation). Mirrors the
// counter/recipient logic that used to live inline in BroadcastDetail so the
// report renders identically.

const STATUS_MAP: Record<BroadcastStatus, SendResultsStatus> = {
  draft: { label: 'draft', tone: 'neutral' },
  sending: { label: 'sending', tone: 'progress' },
  sent: { label: 'sent', tone: 'info' },
  completed: { label: 'completed', tone: 'success' },
  failed: { label: 'failed', tone: 'error' },
};

// Counts mirror the v3/v4 Tally: `sent` is provider-accepted (sent + delivered
// + bounced); opened/clicked count UNIQUE recipients with ≥1 open/click and are
// email-only (omitted for SMS so the component renders an em-dash).
function channelCounters(sends: BroadcastSend[], channel: SendResultsChannel): ChannelCounters {
  let sent = 0;
  let delivered = 0;
  let bounced = 0;
  let failed = 0;
  let opened = 0;
  let clicked = 0;
  let unsubscribed = 0;
  for (const s of sends) {
    if (s.status === 'sent' || s.status === 'delivered' || s.status === 'bounced') sent++;
    if (s.status === 'delivered') delivered++;
    if (s.status === 'bounced') bounced++;
    if (s.status === 'failed') failed++;
    if (s.openCount > 0) opened++;
    if (s.clickCount > 0) clicked++;
    if (s.unsubscribedAt) unsubscribed++;
  }
  const base: ChannelCounters = { sent, delivered, bounced, failed, unsubscribed };
  if (channel === 'email') {
    base.opened = opened;
    base.clicked = clicked;
  }
  return base;
}

function toRecipient(s: BroadcastSend): SendResultsRecipient {
  return {
    contactId: s.contactId,
    contactName: s.contactName,
    to: s.contactEmail ?? s.contactPhone ?? '',
    channel: s.channel,
    status: s.status,
    deliveredAt: s.deliveredAt,
    openedAt: s.openedAt,
    clickCount: s.clickCount,
    firstClickAt: s.firstClickAt,
    unsubscribedAt: s.unsubscribedAt,
    failureReason: s.failureReason,
  };
}

export function broadcastToResultsData(
  broadcast: Broadcast,
  sends: BroadcastSend[],
  opts: { audienceDescription: string; duplicate?: DuplicateAction; deleteAction?: DeleteAction },
): SendResultsData {
  const emailSends = sends.filter((s) => s.channel === 'email');
  const smsSends = sends.filter((s) => s.channel === 'sms');

  const counters: SendResultsData['counters'] = {};
  if (broadcast.channels.includes('email')) counters.email = channelCounters(emailSends, 'email');
  if (broadcast.channels.includes('sms')) counters.sms = channelCounters(smsSends, 'sms');

  return {
    id: broadcast.id,
    title: broadcast.title,
    status: STATUS_MAP[broadcast.status],
    sentAt: broadcast.sentAt,
    source: 'broadcast',
    isInFlight: broadcast.status === 'sending',
    audienceDescription: opts.audienceDescription,
    channels: broadcast.channels,
    fromMode: broadcast.emailFromMode,
    counters,
    // Broadcasts have no preview page.
    pageViews: undefined,
    messagePreview: {
      emailSubject: broadcast.emailSubject,
      emailBodyMarkdown: broadcast.emailBodyMarkdown,
      smsBody: broadcast.smsBody,
    },
    recipients: sends.map(toRecipient),
    actions: { duplicate: opts.duplicate, deleteAction: opts.deleteAction },
  };
}
